import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  CheckCircle2,
  ArrowRight,
  Star,
  Users,
  BarChart3,
  ShieldCheck,
  Zap,
  Building2,
  Gift,
  Search,
  BriefcaseBusiness,
  Newspaper,
  GraduationCap,
  FileText,
  Mic,
  Brain,
  ClipboardCheck,
  Activity,
  MapPin,
  Route,
  Video,
  UserCheck,
  LockKeyhole,
  BadgePoundSterling,
  Database,
} from "lucide-react";
import { toast } from "sonner";
import { useSeo } from "../lib/useSeo";

const SITE_URL = "https://railwaycareers.co.uk";

const TIERS = [
  {
    name: "Free Listing",
    price: "£0",
    priceSuffix: "",
    strapline:
      "For TOCs, FOCs and rail employers who need targeted rail recruitment reach without upfront advertising cost.",
    badge: "Free forever",
    icon: Gift,
    cta: "Create free employer account",
    link: "/employer-portal/register?plan=free",
    features: [
      "Post live UK rail vacancies at no cost",
      "Reach candidates actively searching for railway careers",
      "Direct applicants to your own careers portal or ATS",
      "30-day listing duration",
      "Basic employer profile",
      "Candidate preparation resources support better applicant readiness",
      "Suitable for TOCs, FOCs, contractors, infrastructure organisations and rail suppliers",
    ],
    limits: [
      "Standard search placement",
      "No homepage promotion",
      "No newsletter feature",
    ],
  },
  {
    name: "Featured Vacancy",
    price: "£50",
    priceSuffix: "flat fee",
    strapline:
      "For priority, regional or hard-to-fill roles that need additional candidate visibility.",
    badge: "Single role boost",
    icon: Star,
    cta: "Choose a vacancy to feature",
    link: "#pricing",
    action: "feature-vacancy",
    features: [
      "Everything in Free Listing",
      "Pinned to the top of /vacancies for 30 days",
      "Star-flagged amber styling on every listing card",
      "Eligible for inclusion in the next Monday Briefing newsletter",
      "Priority exposure on relevant role-guide pages",
      "Extra visibility to candidates already researching railway careers",
      "Ideal for trainee driver, conductor, engineering, depot and regional campaigns",
    ],
  },
  {
    name: "Featured Employer",
    price: "£150",
    priceSuffix: "per month",
    strapline:
      "For operators and rail employers that want sustained employer visibility across Railway Careers UK.",
    badge: "Most popular",
    featured: true,
    icon: Building2,
    cta: "Become a Featured Employer",
    link: "#pricing",
    action: "feature-employer",
    features: [
      "Logo on the homepage Featured Employers strip",
      "Featured badge on your company profile",
      "Priority placement in the Operators directory",
      "Regular inclusion in the Railway Careers employer briefing/newsletter",
      "Optional logo and intro paragraph on the cornerstone Train Driver Jobs page",
      "Stronger employer presence alongside rail-specific candidate guidance",
      "Cancel anytime — month-to-month, no long contract",
    ],
    footnote:
      "Featured Employer boosts your profile visibility. You still choose which individual vacancies to feature (£50 each), so you keep full control per role.",
  },
];

const PLAN_OPTIONS = [
  "Employer-ready talent pool",
  "Free Listing",
  "Featured Vacancy",
  "Featured Employer",
  "Custom campaign / partnership",
];

const BENEFITS = [
  {
    icon: Users,
    t: "Reach rail-intent candidates",
    d: "Connect with applicants already searching for UK rail careers, operator campaigns and safety-critical railway roles.",
  },
  {
    icon: ShieldCheck,
    t: "Improve applicant readiness",
    d: "Candidate guidance on CVs, interviews, role aptitude and online assessments helps applicants understand what rail employers expect.",
  },
  {
    icon: BarChart3,
    t: "Reduce recruitment friction",
    d: "Better-informed candidates can reduce weak applications, repeated questions, avoidable drop-off and poor preparation at key stages.",
  },
  {
    icon: Zap,
    t: "Keep applications under your control",
    d: "Post vacancies quickly and direct candidates to your official careers portal, vacancy page or applicant tracking system.",
  },
];

const RESOURCE_AREAS = [
  {
    icon: FileText,
    h: "CV guidance",
    p: "Helps candidates present relevant evidence, safety-critical behaviours, reliability, customer awareness and transferable experience more effectively.",
  },
  {
    icon: Mic,
    h: "Interview preparation",
    p: "Supports applicants in preparing structured examples, behavioural evidence and role-relevant answers before employer-led interview stages.",
  },
  {
    icon: Brain,
    h: "Role aptitude awareness",
    p: "Helps applicants understand the type of aptitude areas commonly associated with railway recruitment, including attention, vigilance, reasoning and coordination.",
  },
  {
    icon: ClipboardCheck,
    h: "Online assessment support",
    p: "Gives applicants a more realistic understanding of early-stage screening, judgement questions and online assessment expectations.",
  },
];

const TRUST_POINTS = [
  {
    icon: Search,
    h: "Built around rail search behaviour",
    p: "Candidates search by role, operator, region and pathway — not just by generic job title. Railway Careers UK is structured around that intent.",
  },
  {
    icon: BriefcaseBusiness,
    h: "Your process remains protected",
    p: "Vacancies send candidates directly to your official application route, keeping selection, assessment, data capture and hiring decisions under your control.",
  },
  {
    icon: Newspaper,
    h: "Recruitment supported by content",
    p: "Role guides, operator pages and careers content help candidates understand the sector before they enter your recruitment process.",
  },
  {
    icon: GraduationCap,
    h: "Preparation before application",
    p: "Free resources help candidates assess whether they are ready, understand the process and submit more considered applications.",
  },
];

const AUDIENCE_CARDS = [
  {
    h: "For Train Operating Companies",
    p: "Promote trainee driver, conductor, dispatcher, customer service, depot and management vacancies to candidates already focused on UK rail careers.",
  },
  {
    h: "For Freight Operating Companies",
    p: "Reach candidates interested in freight driver, ground staff, terminal, engineering, planning and operational railway roles.",
  },
  {
    h: "For rail contractors and suppliers",
    p: "Advertise signalling, track, OLE, rolling stock, project, apprenticeship and specialist rail roles to a more relevant applicant audience.",
  },
];

const TALENT_GATEWAYS = [
  {
    number: "01",
    icon: Brain,
    h: "Role aptitude",
    p: "Role-specific assessments measure capabilities such as concentration, information processing, accuracy, working memory and rule application.",
  },
  {
    number: "02",
    icon: ClipboardCheck,
    h: "Scenario and behaviour profile",
    p: "Candidates are assessed through realistic operational situations covering judgement, risk awareness, procedural discipline, communication and accountability.",
  },
  {
    number: "03",
    icon: FileText,
    h: "Structured CV evidence",
    p: "The uploaded CV is parsed into role-relevant evidence areas. Its content also drives personalised follow-up questions later in the process.",
  },
  {
    number: "04",
    icon: Mic,
    h: "Speech-driven interview simulator",
    p: "Candidates complete an adaptive role-based spoken interview, reducing scripted or copied answers and testing the depth of their evidence.",
  },
  {
    number: "05",
    icon: Video,
    h: "Live professional validation",
    p: "A 45–60 minute Microsoft Teams interview with an experienced recruiting manager tests CV evidence, simulator answers, role motivation and consistency.",
  },
];

const EMPLOYER_POOL_DATA = [
  {
    icon: MapPin,
    h: "Location and travel feasibility",
    p: "Search by postcode-derived journey time, depot, early-start feasibility, transport access and willingness to relocate.",
  },
  {
    icon: Database,
    h: "Comparable candidate evidence",
    p: "View consistent aptitude, scenario, CV and interview metrics for every admitted candidate rather than relying on unstructured applications.",
  },
  {
    icon: UserCheck,
    h: "Human-validated quality",
    p: "Every pool member has passed all mandatory gateways and a final live interview. Candidates who do not meet the standard are not admitted.",
  },
];

// Monthly manual update block:
// Replace these values with your latest 3-month figures each month.
const MANUAL_LAST_3_MONTHS = {
  from_date: "2026-05-05",
  to_date: "2026-07-30",
  days: 87,
  total_search_clicks: 9798,
  best_day_search_clicks: 256,
  total_estimated_visits: 22046,
  avg_daily_estimated_visits: 253,
};

export default function ForEmployers() {
  const [form, setForm] = useState({
    company: "",
    contact_name: "",
    email: "",
    phone: "",
    plan_interest: "Employer-ready talent pool",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [tierLoading, setTierLoading] = useState(null);

  const [visitorMetrics, setVisitorMetrics] = useState({
    today: null,
    live: null,
    updatedAt: null,
    resetTimezone: "Europe/London",
    rolling3Months: null,
  });

  const [metricsStatus, setMetricsStatus] = useState("loading");

  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    let intervalId;

    /*
      Expected response from:

      GET /public/visitor-metrics

      {
        visitors_today: 1248,
        active_now: 18,
        updated_at: "2026-07-30T15:20:00Z"
      }

      Alternative field names are also accepted below.
    */

    const loadVisitorMetrics = async () => {
      try {
        const response = await api.get("/public/visitor-metrics");
        const data = response?.data || {};

        const today = Number(
          data.visitors_today ??
            data.today ??
            data.daily_visitors ??
            data.daily
        );

        const live = Number(
          data.active_now ??
            data.live ??
            data.online_now ??
            data.concurrent
        );

        if (!Number.isFinite(today) || !Number.isFinite(live)) {
          throw new Error(
            "Visitor metrics response is missing numeric values."
          );
        }

        if (mounted) {
          setVisitorMetrics({
            today,
            live,
            updatedAt: data.updated_at || new Date().toISOString(),
            resetTimezone: data.counter_reset_timezone || "Europe/London",
            rolling3Months: data.last_3_months || null,
          });

          setMetricsStatus("ready");
        }
      } catch (error) {
        if (mounted) {
          setMetricsStatus("unavailable");
        }
      }
    };

    loadVisitorMetrics();
    intervalId = window.setInterval(loadVisitorMetrics, 30000);

    return () => {
      mounted = false;
      window.clearInterval(intervalId);
    };
  }, []);

  const formatMetric = (value) =>
    Number.isFinite(value)
      ? new Intl.NumberFormat("en-GB").format(value)
      : "—";

  const metricsUpdatedLabel = visitorMetrics.updatedAt
    ? new Date(visitorMetrics.updatedAt).toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  const last3Months = MANUAL_LAST_3_MONTHS;
  const hasLast3MonthAvg = Number.isFinite(last3Months?.avg_daily_estimated_visits);
  const hasLast3MonthTotal = Number.isFinite(last3Months?.total_estimated_visits);
  const hasLast3MonthBest = Number.isFinite(last3Months?.best_day_search_clicks);

  const handleTierCta = async (tier) => {
    if (!tier.action) {
      navigate(tier.link);
      return;
    }

    if (!user) {
      navigate(
        "/employer-portal/register?next=/for-employers%23pricing"
      );
      return;
    }

    if (tier.action === "feature-vacancy") {
      toast.message("Pick a vacancy to feature", {
        description:
          "Open your dashboard, find the vacancy you want to boost, and click the Feature button.",
      });

      navigate("/employer-portal/dashboard");
      return;
    }

    if (tier.action === "feature-employer") {
      setTierLoading(tier.action);

      try {
        const r = await api.post("/checkout/payment-link", {
          package_id: "featured-employer",
          origin_url: window.location.origin,
        });

        if (!r?.data?.url) {
          throw new Error("No payment link returned.");
        }

        window.location.href = r.data.url;
      } catch (err) {
        toast.error(
          err.response?.data?.detail ||
            "Could not start checkout"
        );

        setTierLoading(null);
      }
    }
  };

  const buildPricingSchema = () => ({
    "@context": "https://schema.org",
    "@type": "OfferCatalog",
    name: "Railway Careers UK Employer Listing Options",
    url: `${SITE_URL}/for-employers#pricing`,
    itemListElement: [
      ...TIERS.map((tier) => {
        const numericPrice = Number(
          (tier.price.match(/\d+/) || ["0"])[0]
        );

        const offer = {
          "@type": "Offer",
          name: tier.name,
          price: numericPrice,
          priceCurrency: "GBP",
          description: tier.strapline,
          url: `${SITE_URL}/for-employers#pricing`,
        };

        if (tier.priceSuffix === "per month") {
          offer.priceSpecification = {
            "@type": "UnitPriceSpecification",
            price: numericPrice,
            priceCurrency: "GBP",
            billingDuration: "P1M",
          };
        }

        return offer;
      }),
      {
        "@type": "Offer",
        name: "Employer-Ready Talent Pool Success Fee",
        price: 350,
        priceCurrency: "GBP",
        description:
          "A flat success fee payable only when a pre-screened candidate from the Railway Careers talent pool starts employment.",
        url: `${SITE_URL}/for-employers#talent-pool`,
      },
    ],
  });

  useSeo({
    title:
      "Rail Recruitment and Pre-Screened Talent Pool | Railway Careers UK",
    description:
      "Reach an active UK rail-careers audience, advertise vacancies and recruit from an employer-ready talent pool whose candidates have passed role aptitude, scenario, CV, simulator and live interview gateways.",
    canonical: "/for-employers",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name:
          "Rail Recruitment and Employer-Ready Talent Pool",
        description:
          "Vacancy advertising and a gateway-controlled talent pool for UK train operators, freight operators, rail contractors and operational employers.",
        url: `${SITE_URL}/for-employers`,
        about: [
          "rail recruitment",
          "TOC recruitment",
          "FOC recruitment",
          "railway job advertising",
          "train operating company vacancies",
          "UK rail jobs",
          "rail candidate preparation",
          "railway aptitude preparation",
          "rail interview preparation",
          "pre-screened rail candidates",
          "rail talent pool",
          "operational recruitment",
        ],
        isPartOf: {
          "@type": "WebSite",
          name: "Railway Careers UK",
          url: SITE_URL,
        },
      },
      buildPricingSchema(),
    ],
  });

  const updateForm = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post("/contact/sales", form);
      setSent(true);

      toast.success(
        "Thanks — we'll be in touch within one working day."
      );
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Could not send"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-navy text-bone min-h-screen">
      <div className="livery-stripe h-1" />

      <main className="max-w-7xl mx-auto px-6 md:px-12 pt-20 pb-20">
        <section className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-amber">
              For TOCs, FOCs, contractors and rail employers
            </div>

            <h1 className="font-display font-extrabold text-3xl sm:text-5xl md:text-6xl tracking-tight leading-[1.02] mt-3">
              Reach rail candidates before your competitors do.
            </h1>

            <p className="text-bone/70 text-lg mt-5 max-w-xl leading-relaxed">
              Railway Careers UK helps train operators, freight
              operators and rail employers reach candidates who are
              already actively exploring railway careers. Post
              vacancies for free, strengthen your employer visibility
              and connect applicants with preparation resources that
              help them understand your recruitment expectations
              before they apply.
            </p>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-md">
              <div>
                <div className="font-mono font-bold text-amber text-3xl">
                  £0
                </div>

                <div className="text-bone/60 text-xs uppercase tracking-wider">
                  Free listing
                </div>
              </div>

              <div>
                <div className="font-mono font-bold text-amber text-3xl">
                  UK
                </div>

                <div className="text-bone/60 text-xs uppercase tracking-wider">
                  Rail focus
                </div>
              </div>

              <div>
                <div className="font-mono font-bold text-amber text-3xl">
                  ATS
                </div>

                <div className="text-bone/60 text-xs uppercase tracking-wider">
                  Direct apply
                </div>
              </div>
            </div>

            <div className="mt-10 space-y-3">
              {[
                "Reach candidates already searching for railway careers, operators and role pathways",
                "Support applicant readiness with CV, interview, role aptitude and online assessment guidance",
                "Reduce avoidable friction caused by poorly informed or underprepared applicants",
                "Keep applicants moving through your official careers page, vacancy portal or ATS",
              ].map((text) => (
                <div
                  key={text}
                  className="flex gap-3 text-sm"
                >
                  <CheckCircle2 className="w-4 h-4 text-amber mt-0.5 shrink-0" />
                  <span>{text}</span>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                asChild
                size="lg"
                className="bg-amber text-slate-950 hover:bg-amber-600 hover:text-slate-950 font-semibold"
                data-testid="emp-hero-register"
              >
                <Link to="/employer-portal/register?plan=free">
                  Post a vacancy for free
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>

              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-bone/30 !text-bone hover:bg-cream/10 hover:!text-bone"
              >
                <a href="#talent-pool">
                  Explore the talent pool
                </a>
              </Button>
            </div>
          </div>

          <div className="space-y-4">
            <div className="relative isolate overflow-hidden rounded-[1.5rem] border border-white/70 bg-gradient-to-br from-white via-slate-50 to-amber-50 p-7 text-slate-950 shadow-[0_24px_80px_-28px_rgba(15,23,42,0.45)] ring-1 ring-slate-200/70 dark:border-slate-700/80 dark:from-slate-950 dark:via-slate-950 dark:to-slate-900 dark:text-slate-50 dark:shadow-[0_28px_90px_-34px_rgba(0,0,0,0.95)] dark:ring-slate-700/60 md:p-8">
              <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-amber-400 via-orange-400 to-sky-400" />
              <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-amber-300/35 blur-2xl dark:bg-amber-400/10" />
              <div className="absolute -left-10 bottom-0 h-28 w-28 rounded-full bg-sky-200/40 blur-2xl dark:bg-sky-500/10" />

              <div className="relative">
                <div className="flex items-center justify-between gap-4">
                  <div className="inline-flex flex-col gap-1 rounded-3xl border border-line bg-surf/95 px-4 py-3 shadow-soft ring-1 ring-line/60">
                    <div className="text-[11px] uppercase tracking-[0.22em] font-extrabold text-slate">
                      Railway Careers audience
                    </div>

                    <h2 className="font-display font-extrabold text-2xl leading-tight text-ice">
                      Candidates are here now.
                    </h2>
                  </div>

                  <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/35 bg-slate-950 px-3 py-1.5 text-xs font-bold text-emerald-300 shadow-lg shadow-slate-950/20 dark:border-emerald-400/35 dark:bg-slate-100 dark:text-emerald-700 dark:shadow-black/20">
                    <span
                      className={`h-2.5 w-2.5 rounded-full ${
                        metricsStatus === "ready"
                          ? "bg-emerald-400 animate-pulse"
                          : "bg-white/60 dark:bg-slate-500"
                      }`}
                    />

                    Live Feed
                  </div>
                </div>

                <div
                  className="mt-7 grid grid-cols-2 gap-3"
                  aria-live="polite"
                  data-testid="visitor-metrics"
                >
                  <div className="rounded-2xl border border-slate-200/80 bg-white/90 p-5 text-ice shadow-[0_18px_40px_-24px_rgba(15,23,42,0.45)] backdrop-blur dark:border-slate-700/80 dark:bg-slate-900/90 dark:text-slate-50">
                    <div className="flex items-center gap-2 text-slate text-xs uppercase tracking-wider">
                      <BarChart3 className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                      Visitors today
                    </div>

                    <div className="mt-3 inline-flex rounded-2xl bg-foreground px-4 py-2 font-mono text-4xl md:text-5xl font-extrabold leading-none tracking-tight text-background shadow-sm">
                      {formatMetric(visitorMetrics.today)}
                    </div>

                    <div className="text-slate text-xs mt-2">
                      Unique daily audience
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200/80 bg-gradient-to-br from-slate-50 to-white p-5 text-ice shadow-[0_18px_40px_-24px_rgba(15,23,42,0.45)] backdrop-blur dark:border-slate-700/80 dark:from-slate-950 dark:to-slate-900 dark:text-slate-50">
                    <div className="flex items-center gap-2 text-slate text-xs uppercase tracking-wider">
                      <Activity className="w-4 h-4 text-slate-700 dark:text-slate-300" />
                      On site now
                    </div>

                    <div className="mt-3 inline-flex rounded-2xl bg-foreground px-4 py-2 font-mono text-4xl md:text-5xl font-extrabold leading-none tracking-tight text-background shadow-sm">
                      {formatMetric(visitorMetrics.live)}
                    </div>

                    <div className="text-slate text-xs mt-2">
                      Active visitors
                    </div>
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
                  <div className="rounded-xl border border-slate-200/80 bg-white/80 px-4 py-3 text-slate-800 shadow-sm dark:border-slate-800 dark:bg-slate-900/80 dark:text-slate-200">
                    <div className="text-[11px] uppercase tracking-wide text-slate">Last 3 months avg/day</div>
                    <div className="mt-1 text-xl font-extrabold text-ice">
                      {hasLast3MonthAvg
                        ? formatMetric(last3Months.avg_daily_estimated_visits)
                        : "—"}
                    </div>
                    <div className="text-[11px] text-slate mt-1">Estimated daily visits</div>
                  </div>

                  <div className="rounded-xl border border-slate-200/80 bg-white/80 px-4 py-3 text-slate-800 shadow-sm dark:border-slate-800 dark:bg-slate-900/80 dark:text-slate-200">
                    <div className="text-[11px] uppercase tracking-wide text-slate">Last 3 months total</div>
                    <div className="mt-1 text-xl font-extrabold text-ice">
                      {hasLast3MonthTotal
                        ? formatMetric(last3Months.total_estimated_visits)
                        : "—"}
                    </div>
                    <div className="text-[11px] text-slate mt-1">Estimated visits ({last3Months?.days || "—"} days)</div>
                  </div>

                  <div className="rounded-xl border border-slate-200/80 bg-white/80 px-4 py-3 text-slate-800 shadow-sm dark:border-slate-800 dark:bg-slate-900/80 dark:text-slate-200">
                    <div className="text-[11px] uppercase tracking-wide text-slate">Strongest day</div>
                    <div className="mt-1 text-xl font-extrabold text-ice">
                      {hasLast3MonthBest
                        ? formatMetric(last3Months.best_day_search_clicks)
                        : "—"}
                    </div>
                    <div className="text-[11px] text-slate mt-1">Organic clicks in a day</div>
                  </div>
                </div>

                <div className="mt-4 rounded-xl border border-slate-200/80 bg-white/80 px-4 py-3 text-xs leading-relaxed text-slate-800 shadow-sm backdrop-blur dark:border-slate-800 dark:bg-slate-900/90 dark:text-slate-200">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <span>
                      Daily visits track live unique visitors and reset at midnight ({visitorMetrics.resetTimezone.replace("_", " ")}); 3-month stats are manually updated monthly.
                    </span>

                    <span className="font-medium text-slate-700 dark:text-slate-300">
                      {metricsUpdatedLabel
                        ? `Updated ${metricsUpdatedLabel}`
                        : metricsStatus === "loading"
                          ? "Loading traffic data…"
                          : "Metrics temporarily unavailable"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {BENEFITS.map((benefit) => {
                const Icon = benefit.icon;

                return (
                  <div
                    key={benefit.t}
                    className="bg-cream/5 border border-bone/10 rounded-lg p-5"
                  >
                    <Icon className="w-5 h-5 text-amber" />

                    <div className="mt-3 font-display font-bold text-sm">
                      {benefit.t}
                    </div>

                    <p className="mt-2 text-xs text-bone/60 leading-relaxed">
                      {benefit.d}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section
          id="talent-pool"
          className="mt-24 scroll-mt-24"
        >
          <div className="mb-8 rounded-xl border border-bone/15 bg-cream/5 p-6 md:p-7">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-amber">
              Planning and approvals tool
            </div>

            <h3 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight mt-2">
              Build your recruitment business case
            </h3>

            <p className="text-bone/70 mt-3 leading-relaxed max-w-4xl">
              Estimate how much recruitment time, operational-management capacity and interview resource your organisation could release by recruiting from the Railway Careers UK Talent Pool.
            </p>

            <p className="text-bone/70 mt-2 leading-relaxed max-w-4xl">
              Enter your own recruitment volumes, salary assumptions and interview arrangements to generate a tailored draft business case and downloadable PDF.
            </p>

            <div className="mt-5">
              <Button
                asChild
                className="bg-amber text-slate-950 hover:bg-amber-600 hover:text-slate-950 font-semibold"
              >
                <Link to="/for-employers/business-case-builder">
                  Build your business case
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            </div>
          </div>

          <div className="mb-8 overflow-hidden rounded-2xl border border-bone/15 bg-navy/50">
            <img
              src="/resources/Talent%20Pool%20Benefits.png"
              alt="Talent Pool benefits overview for rail employers"
              className="w-full h-auto"
              loading="lazy"
            />
            <div className="border-t border-bone/10 bg-navy/70 px-4 py-3 text-sm text-bone/80">
              Employer overview: how the Railway Careers UK Talent Pool can reduce front-end recruitment activity while retaining employer-led checks and final hiring decisions.
            </div>
          </div>

          <div className="overflow-hidden rounded-2xl border border-amber/30 bg-gradient-to-br from-amber/10 via-cream/5 to-cream/5">
            <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-8 p-7 md:p-10 lg:p-12">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-amber">
                  Employer-ready talent pool
                </div>

                <h2 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight mt-3 max-w-3xl">
                  A talent pool candidates must earn their way
                  into.
                </h2>

                <p className="text-bone/70 text-lg mt-5 leading-relaxed max-w-3xl">
                  This is not a pay-to-win candidate database.
                  Every candidate must pass the same role-specific
                  gateways before an approved employer can access
                  their profile. Candidates who do not meet the
                  required standard do not progress into the pool.
                </p>

                <div className="mt-7 grid sm:grid-cols-2 gap-3 max-w-3xl">
                  {[
                    "No candidate fee for pool entry",
                    "No paid route around a failed gateway",
                    "Role-specific evidence and scoring",
                    "Final decision includes experienced human review",
                  ].map((item) => (
                    <div
                      key={item}
                      className="flex items-start gap-3 rounded-lg border border-bone/10 bg-navy/50 p-4 text-sm"
                    >
                      <LockKeyhole className="w-4 h-4 text-amber mt-0.5 shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-xl bg-amber text-ink p-7 md:p-8 shadow-xl">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-extrabold">
                  <BadgePoundSterling className="w-5 h-5" />
                  Straightforward success fee
                </div>

                <div className="mt-5 flex items-end gap-3">
                  <span className="font-display font-extrabold text-4xl sm:text-6xl">
                    £350
                  </span>

                  <span className="font-mono text-sm pb-2">
                    + VAT
                  </span>
                </div>

                <div className="font-display font-bold text-xl mt-1">
                  per candidate who starts employment
                </div>

                <div className="mt-6 space-y-3 text-sm">
                  {[
                    "No percentage of salary",
                    "No charge to browse or shortlist",
                    "No charge for interviews or conditional offers",
                    "Nothing payable if no candidate is employed",
                  ].map((item) => (
                    <div
                      key={item}
                      className="flex gap-3"
                    >
                      <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-6 rounded-lg border border-ink/20 bg-white/70 p-4 text-sm text-slate-900 leading-relaxed">
                  <div className="font-bold uppercase tracking-[0.12em] text-[11px]">
                    Clear value proposition
                  </div>

                  <p className="mt-2">
                    The fee represents a significant saving in work hours otherwise spent progressing and interviewing unsuitable candidates.
                  </p>

                  <p className="mt-2">
                    This quality pipeline is designed to save recruitment teams, HR and operational managers time while protecting interview capacity for stronger candidates.
                  </p>
                </div>

                <Button
                  asChild
                  size="lg"
                  className="mt-7 w-full bg-slate-900 text-white hover:bg-slate-800 hover:text-white font-semibold"
                >
                  <a
                    href="#contact"
                    onClick={() =>
                      updateForm(
                        "plan_interest",
                        "Employer-ready talent pool"
                      )
                    }
                  >
                    Discuss an employer pilot
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </a>
                </Button>
              </div>
            </div>

            <div className="border-t border-bone/10 bg-navy/50 p-7 md:p-10 lg:p-12">
              <div className="max-w-3xl">
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-amber">
                  Five mandatory gateways
                </div>

                <h3 className="font-display font-extrabold text-3xl md:text-4xl mt-2">
                  Progress is earned at every stage.
                </h3>

                <p className="text-bone/60 mt-3 leading-relaxed">
                  Assessment details are retained for approved
                  employer review. The final live interview is
                  recorded and transcribed so that evidence, scoring
                  and interviewer decisions can be checked for
                  accuracy and consistency.
                </p>
              </div>

              <div className="mt-8 grid md:grid-cols-2 xl:grid-cols-5 gap-4">
                {TALENT_GATEWAYS.map((gateway) => {
                  const Icon = gateway.icon;

                  return (
                    <div
                      key={gateway.number}
                      className="rounded-xl border border-bone/10 bg-cream/5 p-5"
                    >
                      <div className="flex items-center justify-between">
                        <Icon className="w-6 h-6 text-amber" />

                        <span className="font-mono text-xs text-bone/40">
                          {gateway.number}
                        </span>
                      </div>

                      <h4 className="font-display font-bold text-lg mt-5">
                        {gateway.h}
                      </h4>

                      <p className="text-bone/60 text-xs leading-relaxed mt-2">
                        {gateway.p}
                      </p>
                    </div>
                  );
                })}
              </div>

              <div className="mt-10 grid lg:grid-cols-3 gap-4">
                {EMPLOYER_POOL_DATA.map((item) => {
                  const Icon = item.icon;

                  return (
                    <div
                      key={item.h}
                      className="rounded-xl border border-amber/20 bg-amber/5 p-6"
                    >
                      <Icon className="w-6 h-6 text-amber" />

                      <h4 className="font-display font-bold text-xl mt-3">
                        {item.h}
                      </h4>

                      <p className="text-bone/60 text-sm leading-relaxed mt-2">
                        {item.p}
                      </p>
                    </div>
                  );
                })}
              </div>

              <div className="mt-8 rounded-xl border border-bone/10 bg-cream/5 p-6 md:p-7">
                <div className="grid md:grid-cols-[auto_1fr] gap-5 items-center">
                  <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-amber text-ink">
                    <Route className="w-7 h-7" />
                  </div>

                  <div>
                    <h4 className="font-display font-bold text-xl">
                      Searchable by postcode-derived travel time
                    </h4>

                    <p className="text-bone/60 text-sm leading-relaxed mt-2">
                      Employers can identify high-quality candidates
                      within a realistic journey time of a depot or
                      workplace, including early-start feasibility
                      and willingness to relocate, without initially
                      exposing a candidate’s full home address.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mt-24 bg-cream/5 border border-bone/10 rounded-xl p-7 md:p-10">
          <div className="grid lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-2">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-amber">
                Why operators need Railway Careers UK
              </div>

              <h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight mt-2">
                Rail recruitment needs more than a vacancy advert.
              </h2>

              <p className="text-bone/60 mt-4 leading-relaxed max-w-3xl">
                Rail employers are recruiting into safety-critical,
                customer-facing, operational and technical roles
                where applicant quality matters. A generic job board
                can generate traffic, but it does not always help
                candidates understand the standards, behaviours,
                assessments and commitment required for railway
                careers. Railway Careers UK is built to close that
                gap.
              </p>
            </div>

            <div className="bg-navy/60 border border-amber/30 rounded-lg p-6">
              <div className="font-display font-bold text-2xl text-amber">
                Recruitment with context
              </div>

              <p className="text-bone/70 text-sm mt-3 leading-relaxed">
                Operators can advertise roles while candidates
                access guidance that helps them prepare more
                realistically before entering your own selection
                process.
              </p>
            </div>
          </div>
        </section>

        <section className="mt-24">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-amber">
              Candidate preparation built in
            </div>

            <h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight mt-2">
              Better-informed applicants before they reach your
              process.
            </h2>

            <p className="text-bone/60 mt-3 leading-relaxed">
              Railway Careers UK helps applicants understand the
              standards, behaviours and preparation expected in rail
              recruitment. For operators, that means vacancies are
              promoted in an environment designed to improve
              candidate understanding before applications reach
              your recruitment team.
            </p>
          </div>

          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {RESOURCE_AREAS.map((item) => {
              const Icon = item.icon;

              return (
                <div
                  key={item.h}
                  className="bg-cream/5 border border-bone/10 rounded-lg p-6"
                >
                  <Icon className="w-6 h-6 text-amber" />

                  <h3 className="font-display font-bold text-xl mt-3">
                    {item.h}
                  </h3>

                  <p className="text-bone/60 text-sm mt-2 leading-relaxed">
                    {item.p}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        <section className="mt-24 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-amber">
              Built around rail recruitment
            </div>

            <h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight mt-2">
              Not a generic job board.
            </h2>

            <p className="text-bone/60 mt-4 leading-relaxed">
              Railway Careers UK is designed around how candidates
              actually search for railway work: trainee train driver
              jobs, conductor vacancies, engineering
              apprenticeships, TOC campaigns, depot roles, operator
              pages and regional rail opportunities.
            </p>

            <p className="text-bone/60 mt-4 leading-relaxed">
              The platform supports both sides of the recruitment
              journey. Employers gain targeted vacancy visibility
              and direct apply links, while candidates gain free
              guidance on CVs, interviews, role aptitude and online
              assessments so they can make more realistic,
              better-prepared applications.
            </p>
          </div>

          <div className="grid gap-4">
            {TRUST_POINTS.map((item) => {
              const Icon = item.icon;

              return (
                <div
                  key={item.h}
                  className="bg-cream/5 border border-bone/10 rounded-lg p-6"
                >
                  <div className="flex gap-4">
                    <Icon className="w-6 h-6 text-amber shrink-0 mt-1" />

                    <div>
                      <h3 className="font-display font-bold text-xl">
                        {item.h}
                      </h3>

                      <p className="text-bone/60 text-sm mt-2 leading-relaxed">
                        {item.p}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section
          id="pricing"
          className="mt-24 scroll-mt-24"
        >
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-amber">
              Employer listing options
            </div>

            <h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight mt-2">
              Start free. Upgrade when visibility matters.
            </h2>

            <p className="text-bone/60 mt-3 leading-relaxed">
              Standard vacancy listings are free for rail employers.
              Paid options are available for featured visibility,
              high-volume recruitment, regional campaigns and
              year-round employer presence. Every listing sits
              within a wider careers platform that helps candidates
              prepare for CV screening, interviews, role aptitude
              and online assessments before they apply through your
              official route.
            </p>
          </div>

          <div className="mt-8 grid md:grid-cols-3 gap-5">
            {TIERS.map((tier) => {
              const Icon = tier.icon;

              const testId = tier.name
                .toLowerCase()
                .replace(/\s+/g, "-");

              return (
                <div
                  key={tier.name}
                  className={`rounded-lg p-7 flex flex-col relative ${
                    tier.featured
                      ? "bg-amber text-ink shadow-2xl ring-2 ring-amber/60 md:-translate-y-2"
                      : "bg-cream/5 border border-bone/10 text-bone"
                  }`}
                  data-testid={`tier-${testId}`}
                >
                  {tier.featured ? (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-ink text-amber text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1 rounded-full shadow">
                      {tier.badge}
                    </div>
                  ) : (
                    <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-bold text-amber">
                      <Icon className="w-4 h-4" />
                      {tier.badge}
                    </div>
                  )}

                  <div
                    className={`font-display font-extrabold text-2xl ${
                      tier.featured ? "mt-4" : "mt-3"
                    }`}
                  >
                    {tier.name}
                  </div>

                  <div className="mt-2 flex items-baseline gap-2">
                    <span className="font-display text-4xl font-extrabold">
                      {tier.price}
                    </span>

                    {tier.priceSuffix && (
                      <span
                        className={`text-xs font-mono ${
                          tier.featured
                            ? "text-ink/70"
                            : "text-bone/60"
                        }`}
                      >
                        {tier.priceSuffix}
                      </span>
                    )}
                  </div>

                  <p
                    className={`mt-4 text-sm leading-relaxed ${
                      tier.featured
                        ? "text-ink/80"
                        : "text-bone/60"
                    }`}
                  >
                    {tier.strapline}
                  </p>

                  <ul className="mt-5 space-y-2 text-sm flex-1">
                    {tier.features.map((feature) => (
                      <li
                        key={feature}
                        className="flex gap-2"
                      >
                        <CheckCircle2
                          className={`w-4 h-4 shrink-0 mt-0.5 ${
                            tier.featured
                              ? "text-ink"
                              : "text-amber"
                          }`}
                        />

                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {tier.limits && (
                    <div
                      className={`mt-5 pt-4 border-t text-xs leading-relaxed ${
                        tier.featured
                          ? "border-ink/20 text-ink/70"
                          : "border-bone/10 text-bone/50"
                      }`}
                    >
                      {tier.limits.join(" · ")}
                    </div>
                  )}

                  {tier.footnote && (
                    <div
                      className={`mt-5 pt-4 border-t text-xs leading-relaxed italic ${
                        tier.featured
                          ? "border-ink/20 text-ink/70"
                          : "border-bone/10 text-bone/50"
                      }`}
                    >
                      {tier.footnote}
                    </div>
                  )}

                  {tier.action ? (
                    <Button
                      onClick={() => handleTierCta(tier)}
                      disabled={tierLoading === tier.action}
                      className={`mt-6 w-full font-semibold ${
                        tier.featured
                          ? "bg-slate-900 text-white hover:bg-lime-600 hover:text-slate-950"
                          : "bg-amber text-slate-950 hover:bg-amber-600 hover:text-slate-950"
                      }`}
                      data-testid={`tier-cta-${testId}`}
                    >
                      {tierLoading === tier.action
                        ? "Redirecting to Stripe…"
                        : tier.cta}
                    </Button>
                  ) : (
                    <Button
                      asChild
                      className={`mt-6 w-full font-semibold ${
                        tier.featured
                          ? "bg-slate-900 text-white hover:bg-lime-600 hover:text-slate-950"
                          : "bg-amber text-slate-950 hover:bg-amber-600 hover:text-slate-950"
                      }`}
                      data-testid={`tier-cta-${testId}`}
                    >
                      <Link to={tier.link}>
                        {tier.cta}
                      </Link>
                    </Button>
                  )}
                </div>
              );
            })}
          </div>

          <p className="mt-6 text-sm text-bone/60 max-w-3xl">
            Need a larger recruitment push — multiple vacancies, a
            regional campaign, trainee intake, apprenticeship
            campaign or year-round employer partnership?{" "}
            <a
              href="#contact"
              className="text-amber font-semibold hover:underline"
              data-testid="tier-bulk-link"
            >
              Talk to us about a custom campaign →
            </a>
          </p>
        </section>

        <section className="mt-24 grid lg:grid-cols-3 gap-5">
          {AUDIENCE_CARDS.map((item) => (
            <div
              key={item.h}
              className="bg-cream/5 border border-bone/10 rounded-lg p-6"
            >
              <h3 className="font-display font-bold text-xl">
                {item.h}
              </h3>

              <p className="text-bone/60 text-sm mt-3 leading-relaxed">
                {item.p}
              </p>
            </div>
          ))}
        </section>

        <section className="mt-24 bg-cream/5 border border-bone/10 rounded-xl p-7 md:p-10">
          <div className="grid lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-2">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-amber">
                Employer value
              </div>

              <h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight mt-2">
                Visibility is only part of the offer.
              </h2>

              <p className="text-bone/60 mt-4 leading-relaxed max-w-3xl">
                Operators do not just need more clicks. They need
                relevant candidates who understand the railway
                environment, the selection process and the standards
                expected in safety-critical and customer-facing
                roles. Railway Careers UK combines targeted vacancy
                visibility with candidate education, helping
                employers attract a more informed applicant pool.
                That means fewer hours lost progressing and
                interviewing unsuitable applicants, and more time
                focused on candidates with stronger fit and
                readiness.
              </p>
            </div>

            <div className="bg-navy/60 border border-amber/30 rounded-lg p-6">
              <div className="font-display font-bold text-2xl text-amber">
                Save time across the hiring chain
              </div>

              <p className="text-bone/70 text-sm mt-3 leading-relaxed">
                The fee is positioned as a practical time-saving
                investment: a quality pipeline that helps recruitment
                teams, HR and operational managers spend less time on
                unsuitable candidates and more time on viable hires.
              </p>
            </div>
          </div>
        </section>

        <section
          id="contact"
          className="mt-24 grid md:grid-cols-2 gap-12 items-start scroll-mt-24"
        >
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-amber">
              Talk to us
            </div>

            <h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight mt-2">
              Need stronger candidates or greater campaign
              visibility?
            </h2>

            <p className="text-bone/60 mt-3 leading-relaxed">
              Tell us about the role, location, intake or campaign.
              We can discuss access to the employer-ready talent
              pool, a pilot for a high-volume operational role, or
              vacancy promotion through the existing employer
              platform.
            </p>

            <div className="mt-6">
              <Button
                asChild
                size="lg"
                className="bg-amber text-slate-950 hover:bg-amber-600 hover:text-slate-950 font-semibold"
              >
                <Link to="/employer-portal/register?plan=free">
                  Create free employer account
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            </div>

            <div className="mt-8 bg-cream/5 border border-bone/10 rounded-lg p-6">
              <h3 className="font-display font-bold text-xl">
                For standard listings
              </h3>

              <p className="text-bone/60 text-sm mt-2 leading-relaxed">
                Use the free employer account route to post
                vacancies directly. The form is best for featured
                visibility, campaign packages, partnerships,
                employer branding or questions before registering.
              </p>
            </div>
          </div>

          {sent ? (
            <div className="bg-cream/5 border border-amber rounded-lg p-8 text-center">
              <CheckCircle2 className="w-10 h-10 text-amber mx-auto" />

              <div className="font-display font-bold text-xl mt-3">
                Thanks — message received.
              </div>

              <p className="text-bone/70 mt-2 text-sm">
                We'll be in touch shortly.
              </p>
            </div>
          ) : (
            <form
              onSubmit={submit}
              className="bg-cream/5 border border-bone/10 rounded-lg p-7 space-y-4"
              data-testid="sales-form"
            >
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label
                    htmlFor="sales-company"
                    className="text-bone/80"
                  >
                    Company
                  </Label>

                  <Input
                    id="sales-company"
                    className="mt-1 bg-navy-700 border-bone/20 text-bone"
                    required
                    value={form.company}
                    onChange={(e) =>
                      updateForm("company", e.target.value)
                    }
                    data-testid="sales-company"
                  />
                </div>

                <div>
                  <Label
                    htmlFor="sales-name"
                    className="text-bone/80"
                  >
                    Your name
                  </Label>

                  <Input
                    id="sales-name"
                    className="mt-1 bg-navy-700 border-bone/20 text-bone"
                    required
                    value={form.contact_name}
                    onChange={(e) =>
                      updateForm(
                        "contact_name",
                        e.target.value
                      )
                    }
                    data-testid="sales-name"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label
                    htmlFor="sales-email"
                    className="text-bone/80"
                  >
                    Work email
                  </Label>

                  <Input
                    id="sales-email"
                    className="mt-1 bg-navy-700 border-bone/20 text-bone"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) =>
                      updateForm("email", e.target.value)
                    }
                    data-testid="sales-email"
                  />
                </div>

                <div>
                  <Label
                    htmlFor="sales-phone"
                    className="text-bone/80"
                  >
                    Phone optional
                  </Label>

                  <Input
                    id="sales-phone"
                    className="mt-1 bg-navy-700 border-bone/20 text-bone"
                    value={form.phone}
                    onChange={(e) =>
                      updateForm("phone", e.target.value)
                    }
                    data-testid="sales-phone"
                  />
                </div>
              </div>

              <div>
                <Label
                  htmlFor="sales-plan"
                  className="text-bone/80"
                >
                  Interested in
                </Label>

                <select
                  id="sales-plan"
                  className="mt-1 w-full rounded-md bg-navy-700 border border-bone/20 text-bone px-3 py-2"
                  value={form.plan_interest}
                  onChange={(e) =>
                    updateForm(
                      "plan_interest",
                      e.target.value
                    )
                  }
                  data-testid="sales-plan"
                >
                  {PLAN_OPTIONS.map((option) => (
                    <option
                      key={option}
                      value={option}
                    >
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label
                  htmlFor="sales-message"
                  className="text-bone/80"
                >
                  What do you need to hire?
                </Label>

                <Textarea
                  id="sales-message"
                  className="mt-1 bg-navy-700 border-bone/20 text-bone min-h-[120px]"
                  required
                  value={form.message}
                  onChange={(e) =>
                    updateForm("message", e.target.value)
                  }
                  data-testid="sales-message"
                  placeholder="Example: we recruit conductors across three North West depots and want a postcode-filtered pool of candidates who have passed all screening gateways."
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="bg-amber text-slate-950 hover:bg-amber-600 hover:text-slate-950 w-full font-semibold"
                data-testid="sales-submit"
              >
                {loading
                  ? "Sending…"
                  : "Contact Railway Careers"}
              </Button>

              <p className="text-xs text-bone/40 leading-relaxed">
                Standard vacancy listings can be posted through the
                free employer account route. This form is best for
                talent-pool pilots, featured campaigns, employer
                partnerships or questions before registering.
              </p>
            </form>
          )}
        </section>
      </main>
    </div>
  );
}